/**
 * Forces the browser's HTTP cache to re-fetch `index.html` before the caller reloads.
 *
 * The deploy serves `index.html` with **no `Cache-Control`** (S3/CloudFront send only
 * `Last-Modified`), so browsers fall back to heuristic freshness — roughly a tenth of
 * the document's age, i.e. hours — and a plain `location.reload()` is a *normal*
 * reload: it may be satisfied from the HTTP cache without a single network request.
 * The tab then comes back on the very build the user asked to leave, appVersionWatch
 * spots the same mismatch (it defeats caching with `no-store` + a unique query, so it
 * always sees the truth), and the prompt returns — the reload button looks broken.
 * Safari is where this shows up most, but nothing about it is Safari-specific.
 *
 * `cache: 'reload'` bypasses the cache on the way out **and stores the fresh response**,
 * so the reload that follows is served the new shell. Run it *after*
 * `activateNewestServiceWorker`: with a worker in control this fetch goes through it,
 * and only the newly-activated worker holds the new precache.
 *
 * Best-effort and time-bounded — the caller navigates either way, so a stalled network
 * must never be able to strand it.
 */

const REVALIDATE_TIMEOUT_MS = 3_000

export async function revalidateAppShell(): Promise<void> {
  try {
    await Promise.race([
      fetch(window.location.href, {
        cache: 'reload',
        credentials: 'same-origin',
      }),
      new Promise<void>((resolve) =>
        window.setTimeout(resolve, REVALIDATE_TIMEOUT_MS),
      ),
    ])
  } catch {
    /* best-effort — the caller reloads regardless */
  }
}
